import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Grid,
  Box,
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  InputAdornment,
  IconButton,
  FormHelperText,
  LinearProgress,
  CircularProgress,
  Divider
} from '@mui/material';
import { useFormik } from 'formik';
import { hotelSchema } from 'schema';
import { patchApi } from 'views/services/api';
import { toast } from 'react-toastify';
import { useState } from 'react';
import { getTimezones } from 'utils/timeZone';
import { getCountries } from 'utils/countries';
import PropTypes from 'prop-types';

// icons
import ClearIcon from '@mui/icons-material/Clear';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';

const EditHotel = (props) => {
  const { open, handleClose, data } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = {
    email: data?.email || '',
    name: data?.name || '',
    managerName: data?.managerName || '',
    contact: data?.contact || '',
    managerPhone: data?.managerPhone || '',
    country: data?.country || '',
    address: data?.address || '',
    currency: data?.currency || 'USD',
    timezone: data?.timezone || 'America/Caracas',
    gstNumber: data?.gstNumber || '',
    foodgstpercentage: data?.foodgstpercentage || '',
    roomgstpercentage: data?.roomgstpercentage || '',
    mapurl: data?.mapurl || ''
  };

  const handleUpdate = async (values) => {
    setIsSubmitting(true);
    try {
      let response = await patchApi(`api/hotel/edit/${data?._id}`, values);
      if (response.status === 200) {
        toast.success('¡Hotel actualizado exitosamente!');
        handleClose();
      } else {
        toast.error(response.response?.data?.message || 'Error al actualizar el hotel');
      }
    } catch (e) {
      console.error(e);
      toast.error('Ocurrió un error inesperado al intentar actualizar el hotel');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: hotelSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      handleUpdate(values);
    }
  });

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: { borderRadius: '12px', overflow: 'hidden' }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)',
        color: 'white'
      }}>
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 600 }}>
          Editar Información del Hotel
        </Typography>
        <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 4 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Name */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={formik.touched.name && Boolean(formik.errors.name)}>
                <InputLabel>Nombre del Hotel</InputLabel>
                <OutlinedInput
                  id="name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  startAdornment={
                    <InputAdornment position="start">
                      <BusinessOutlinedIcon color="action" />
                    </InputAdornment>
                  }
                  label="Nombre del Hotel"
                />
                {formik.touched.name && formik.errors.name && (
                  <FormHelperText error>{formik.errors.name}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Persona Encargada */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Persona Encargada</InputLabel>
                <OutlinedInput
                  id="managerName"
                  name="managerName"
                  value={formik.values.managerName}
                  onChange={formik.handleChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <BadgeOutlinedIcon color="action" />
                    </InputAdornment>
                  }
                  label="Persona Encargada"
                />
              </FormControl>
            </Grid>

            {/* Contact */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={formik.touched.contact && Boolean(formik.errors.contact)}>
                <InputLabel>Teléfono del Hotel</InputLabel>
                <OutlinedInput
                  id="contact"
                  name="contact"
                  type="text"
                  value={formik.values.contact}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  startAdornment={
                    <InputAdornment position="start">
                      <PhoneIphoneOutlinedIcon color="action" />
                    </InputAdornment>
                  }
                  label="Teléfono del Hotel"
                  placeholder="+58 212... (formato internacional)"
                />
                {formik.touched.contact && formik.errors.contact && (
                  <FormHelperText error>{formik.errors.contact}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Teléfono del Encargado */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Teléfono del Encargado</InputLabel>
                <OutlinedInput
                  id="managerPhone"
                  name="managerPhone"
                  value={formik.values.managerPhone}
                  onChange={formik.handleChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <PhoneIphoneOutlinedIcon color="action" />
                    </InputAdornment>
                  }
                  label="Teléfono del Encargado"
                  placeholder="+58 414... (formato internacional)"
                />
              </FormControl>
            </Grid>

            {/* País */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>País</InputLabel>
                <Select
                  id="country"
                  name="country"
                  native
                  value={formik.values.country}
                  onChange={formik.handleChange}
                  input={
                    <OutlinedInput
                      label="País"
                      startAdornment={
                        <InputAdornment position="start">
                          <PublicOutlinedIcon color="action" />
                        </InputAdornment>
                      }
                    />
                  }
                >
                  <option value="">Selecciona un país</option>
                  {getCountries().map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Email (Disabled) */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled>
                <InputLabel>Correo Electrónico</InputLabel>
                <OutlinedInput
                  id="email"
                  name="email"
                  value={formik.values.email}
                  label="Correo Electrónico"
                  startAdornment={
                    <InputAdornment position="start">
                      <LanguageOutlinedIcon color="action" />
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Grid>

            {/* GST Number */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>RIF / Identificación Fiscal</InputLabel>
                <OutlinedInput
                  id="gstNumber"
                  name="gstNumber"
                  value={formik.values.gstNumber}
                  onChange={formik.handleChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <ReceiptOutlinedIcon color="action" />
                    </InputAdornment>
                  }
                  label="RIF / Identificación Fiscal"
                />
              </FormControl>
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <FormControl fullWidth error={formik.touched.address && Boolean(formik.errors.address)}>
                <InputLabel>Dirección Física</InputLabel>
                <OutlinedInput
                  id="address"
                  name="address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationOnOutlinedIcon color="action" />
                    </InputAdornment>
                  }
                  label="Dirección Física"
                  multiline
                  rows={2}
                />
                {formik.touched.address && formik.errors.address && (
                  <FormHelperText error>{formik.errors.address}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Map URL */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>URL de Google Maps</InputLabel>
                <OutlinedInput
                  id="mapurl"
                  name="mapurl"
                  value={formik.values.mapurl}
                  onChange={formik.handleChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <MapOutlinedIcon color="action" />
                    </InputAdornment>
                  }
                  label="URL de Google Maps"
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="caption" color="textSecondary">CONFIGURACIÓN</Typography>
              </Divider>
            </Grid>

            {/* Currency */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Moneda Principal</InputLabel>
                <Select
                  id="currency"
                  name="currency"
                  native
                  value={formik.values.currency}
                  onChange={formik.handleChange}
                  input={
                    <OutlinedInput 
                      label="Moneda Principal" 
                      startAdornment={
                        <InputAdornment position="start">
                          <MonetizationOnOutlinedIcon color="action" />
                        </InputAdornment>
                      }
                    />
                  }
                >
                  <option value="USD">USD - Dólar estadounidense</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="MXN">MXN - Peso mexicano</option>
                  <option value="ARS">ARS - Peso argentino</option>
                  <option value="BOB">BOB - Boliviano</option>
                  <option value="BRL">BRL - Real brasileño</option>
                  <option value="CLP">CLP - Peso chileno</option>
                  <option value="COP">COP - Peso colombiano</option>
                  <option value="CRC">CRC - Colón costarricense</option>
                  <option value="CUP">CUP - Peso cubano</option>
                  <option value="DOP">DOP - Peso dominicano</option>
                  <option value="GTQ">GTQ - Quetzal guatemalteco</option>
                  <option value="HNL">HNL - Lempira hondureño</option>
                  <option value="NIO">NIO - Córdoba nicaragüense</option>
                  <option value="PAB">PAB - Balboa panameño</option>
                  <option value="PEN">PEN - Sol peruano</option>
                  <option value="PYG">PYG - Guaraní paraguayo</option>
                  <option value="UYU">UYU - Peso uruguayo</option>
                  <option value="VES">VES - Bolívar venezolano</option>
                </Select>
              </FormControl>
            </Grid>

            {/* Timezone */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Zona Horaria</InputLabel>
                <Select
                  id="timezone"
                  name="timezone"
                  native
                  value={formik.values.timezone}
                  onChange={formik.handleChange}
                  input={
                    <OutlinedInput 
                      label="Zona Horaria" 
                      startAdornment={
                        <InputAdornment position="start">
                          <LanguageOutlinedIcon color="action" />
                        </InputAdornment>
                      }
                    />
                  }
                >
                  {getTimezones().map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* GST Percentages */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>IVA Alimentos (%)</InputLabel>
                <OutlinedInput
                  id="foodgstpercentage"
                  name="foodgstpercentage"
                  type="number"
                  value={formik.values.foodgstpercentage}
                  onChange={formik.handleChange}
                  label="IVA Alimentos (%)"
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>IVA Habitaciones (%)</InputLabel>
                <OutlinedInput
                  id="roomgstpercentage"
                  name="roomgstpercentage"
                  type="number"
                  value={formik.values.roomgstpercentage}
                  onChange={formik.handleChange}
                  label="IVA Habitaciones (%)"
                />
              </FormControl>
            </Grid>
          </Grid>
          
          {isSubmitting && (
            <Box sx={{ width: '100%', mt: 3 }}>
              <LinearProgress />
            </Box>
          )}
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
        <Button onClick={handleClose} variant="text" color="inherit">
          Cancelar
        </Button>
        <Button 
          onClick={formik.handleSubmit} 
          variant="contained" 
          disabled={isSubmitting}
          sx={{ 
            minWidth: 150,
            background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)',
            boxShadow: '0 3px 5px 2px rgba(255, 152, 0, .3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #F57C00 30%, #FF9800 90%)',
            }
          }}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Guardar Cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditHotel.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  data: PropTypes.object
};

export default EditHotel;
